import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { AuthShell, safeRedirect } from "@/components/site/AuthShell";
import { Loading, Notice, ProblemAlert } from "@/components/site/Feedback";
import { api } from "@/lib/api";
import { useRunOnce } from "@/lib/hooks";
import { authStore } from "@/lib/auth-store";
import type { EmailLinkSearch } from "@/router";

/**
 * Email links: /identity/account/ConfirmEmail?userId=&code=[&returnUrl=] and
 * /identity/account/ConfirmEmailChange?userId=&email=&code= (same as the Blazor ConfirmEmail page).
 */
export function ConfirmEmailPage({ change }: { change: boolean }) {
  usePageTitle(change ? "Confirm email change" : "Confirm email");
  const search = useRouterState({ select: (s) => s.location.search }) as EmailLinkSearch;
  const { userId, code, email, returnUrl } = search;
  const invalid = !userId || !code || (change && !email);
  const router = useRouter();

  const confirm = useRunOnce(
    [change ? "confirm-email-change" : "confirm-email", userId, code, email],
    // A query must not resolve to undefined (the endpoints return 204).
    async () => {
      await (change ? api.account.confirmEmailChange(userId!, email!, code!) : api.auth.confirmEmail(userId!, code!));
      return true;
    },
    !invalid,
  );

  const signedIn = authStore.isAuthenticated();
  const next = returnUrl ? safeRedirect(returnUrl, "/login") : signedIn ? "/dashboard" : "/login";

  return (
    <AuthShell eyebrow="Members' portal" title={change ? "Confirm your new email" : "Confirm your email"}>
      {invalid ? (
        <ProblemAlert error={new Error("This link is invalid or incomplete. Use the latest email we sent you.")} />
      ) : confirm.isPending ? (
        <Loading label="Confirming…" />
      ) : confirm.isError ? (
        <ProblemAlert error={confirm.error} />
      ) : (
        <Notice tone="success" title={change ? "Your email address was changed" : "Your email is confirmed"}>
          {change
            ? `From now on, sign in with ${email}.`
            : "Your account is active. Sign in to get started."}
        </Notice>
      )}
      <Button variant="gold" size="lg" className="mt-8 w-full" onClick={() => router.history.push(next)}>
        Continue
      </Button>
      {!change && confirm.isError && (
        <p className="mt-4 text-sm text-muted-foreground">
          Link expired?{" "}
          <Link to="/resend-confirmation" search={{ email: undefined }} className="underline underline-offset-4">
            Send a new confirmation link
          </Link>
        </p>
      )}
    </AuthShell>
  );
}
